import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getTime, scrollToBottom } from "./ChatHandler";
import { Message } from "./ChatInterface";
import { fetchCampaignListsAndCompany } from "src/services/EngagementAPIs";
import {
  fetchChatMessages,
  getActiveChatSessionId,
  resetChatSession,
  setActiveChatSessionId,
  syncChatbotMessageNumber,
} from "src/services/ChatbotAPI";
import { ChatImagePreview } from "src/components/common/MessageBubble";
import { getApiOriginUrl } from "src/config/Endpoints";
import { useLocation } from "react-router-dom";
import { Avatar, message } from "antd";
import ChatInput from "./ChatInput";
import "./ChatScreen.scss";
import { ChatHistorySidebar, ChatSession } from "./ChatHistorySidebar";
import { createMessageHandler } from "src/handlers/MessageHandlers";
import ChatCampaignWizard from "./CampaignWizard/ChatCampaignWizard";
import ChatTemplateCampaignWizard from "./CampaignWizard/ChatTemplateCampaignWizard";
import ChatWhatsAppCampaignWizard from "./CampaignWizard/ChatWhatsAppCampaignWizard";
import ChatChannelChoice from "./CampaignWizard/ChatChannelChoice";
import ChatChannelReplies from "./CampaignWizard/ChatChannelReplies";
import ChatWhatsAppModeChoice from "./CampaignWizard/ChatWhatsAppModeChoice";
import ChatCampaignSetupStep from "./CampaignWizard/steps/ChatCampaignSetupStep";
import { useCampaignStatus, useCampaignStatusSync } from "src/hooks/UseCampaignStatusSync";
import {
  runAfterExitConfirm,
  useExitCampaignGuard,
} from "src/layouts/Engagements/Campaigns/shared/ExitCampaignGuard";
import { parseCampaignFlow, persistCampaignStep } from "./CampaignWizard/PersistCampaignStep";
import { buildChoiceAnswerMessages } from "./CampaignWizard/ChatChoiceAnswer";
import { useChatCampaignFlow } from "./CampaignWizard/UseChatCampaignFlow";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  audienceListMatchesSearch,
  CREATE_NEW_LIST_NAME,
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";
import { Volume2, ImagePlus, ChartLine, CircleDollarSign, ListPlus, Search } from "lucide-react";

const API_ORIGIN = getApiOriginUrl();

const chatScreenTranslationKeys = [
  "unableToLoadSession",
  "missingApiCredentials",
  "createNewList",
  "noCategoriesDetected",
  "failedToFetchCategories",
  "roasReply",
  "conversionsReply",
  "impressionsReply",
  "engagementReply",
  "createCampaignReply",
  "templateGenerationError",
  "failedToGenerateTemplate",
  "noCategoriesSelected",
  "selectedCategories",
  "selectCampaignCategory",
  "okayNoProblem",
  "showCampaignPerformance",
  "viewReports",
  "clearChat",
  "newChat",
  "toggleSidebar",
  "greetingTitle",
  "greetingSubtitle",
  "suggestionsLabel",
  "returnOnAdSpend",
  "engagementAndLikes",
  "createNewCampaign",
  "conversions",
  "impressions",
  "fetchedCategoriesPrompt",
  "searchCategoriesPlaceholder",
  "noCategoriesMatchSearch",
  "showMoreCategories",
  "proceed",
  "customCampaignCreated",
  "generatedTemplate",
  "previewTemplate",
  "readyToSend",
  "yes",
  "no",
  "edit",
  "sendOrSchedule",
  "alreadySentWarning",
  "alreadySentScheduleWarning",
  "sendNow",
  "schedule",
  "scheduleCampaign",
  "pakistanTime",
  "tomorrowMorning",
  "tomorrowEvening",
  "nextMonday",
  "selectDate",
  "selectTime",
  "cannotEditConfirmedSchedule",
  "cancel",
  "yesSchedule",
  "noChangeTime",
  "relatedQuestions",
  "selectYourList",
  "searchListsPlaceholder",
  "noListsMatchSearch",
  "createCustomList",
  "typing",
  "suggestedQuestionsHeader",
  "sentCannotEdit",
  "cannotRescheduleSentCampaign",
  "quickActionCreateCampaignTitle",
  "quickActionCreateCampaignSubtitle",
  "quickActionAnalyseScreenshotTitle",
  "quickActionAnalyseScreenshotSubtitle",
  "quickActionEngagementImpressionsTitle",
  "quickActionEngagementImpressionsSubtitle",
  "quickActionRoasTitle",
  "quickActionRoasSubtitle",
  "quickActionCreateListTitle",
  "quickActionCreateListSubtitle",
] as const;

type ChatScreenTranslationKey = typeof chatScreenTranslationKeys[number];
type ChatScreenText = Record<ChatScreenTranslationKey, string>;
type ChatScreenLocationState = {
  preserveSession?: boolean;
  liveChatState?: {
    query?: Message[];
    firstMessageSent?: boolean;
    suggestedQuestions?: string[];
    conversationStep?: any;
    campaignName?: string;
    audience?: string;
  };
};
const Chatbot = () => {
  const { t: translate, i18n } = useTranslation();
  useLanguage();
  const isArabic = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en"
  ).toLowerCase().startsWith("ar");
  const text: ChatScreenText = React.useMemo(
    () =>
      chatScreenTranslationKeys.reduce((acc, key) => {
        acc[key] = translate(`chatScreen.${key}`);
        return acc;
      }, {} as ChatScreenText),
    [translate, i18n.resolvedLanguage, i18n.language],
  );
  const chatbotRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState<Message[]>([]);
  useCampaignStatusSync(query);
  const exitGuard = useExitCampaignGuard(useCampaignStatus() === "in_progress");
  const presetChannel = [...query].reverse().find(msg => msg.type === "campaignWizard")?.campaignChannel;
  const campaignFlow = useChatCampaignFlow({
    presetChannel,
    onAdvance: snapshot => {
      persistCampaignStep(snapshot, translate);
      setQuery(prev => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          if (updated[i].type === "campaignWizard") {
            updated[i] = { ...updated[i], campaignFlowSnapshot: snapshot };
            break;
          }
        }
        return [
          ...updated,
          ...buildChoiceAnswerMessages(snapshot, translate),
          { sender: "bot", timestamp: getTime(), type: "campaignWizard", campaignChannel: presetChannel },
        ];
      });
    },
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isScheduledOrSent] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | File[] | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [conversationStep, setConversationStep] = useState<
    | "impressionsStep"
    | "roasStep"
    | "conversionsStep"
    | "engagementStep"
    | "askAudience"
    | "askWebsite"
    | "confirmCampaign"
    | null
  >(null);
  const clearedRef = useRef(false);
  const [campaignName] = useState("");
  const user_id = localStorage.getItem("user_id");
  const tenant_id = localStorage.getItem("tenant_id") || "";
  const [, setPosition] = useState({
    x: window.innerWidth - 100,
    y: window.innerHeight - 100,
  });
  const dragging = useRef(false);
  const dragged = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const [audiences, setAudiences] = useState<AudienceListOption[]>([]);
  const [audienceListSearch, setAudienceListSearch] = useState("");
  const isScheduledOrSentRef = useRef(isScheduledOrSent);
  const [, setInputDisabled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  /** `undefined` = not limited; string (from API `detail`) = show in thread + under input */
  const [queryLimitApiMessage, setQueryLimitApiMessage] = useState<string | undefined>(undefined);
  const location = useLocation();
  const locationState = location.state as ChatScreenLocationState | null;
  const consumedLiveStateRef = useRef(false);

  const getFileFromHistory = (message: any): Message["file"] | null => {
    const fileData =
      message?.file_data ||
      message?.fileData ||
      message?.file?.data ||
      message?.file?.file_data;
    const fileUrl =
      message?.image_url ||
      message?.file_url ||
      message?.fileUrl ||
      message?.file?.url ||
      message?.file?.file_url ||
      message?.file?.fileUrl;
    const url = fileData || fileUrl;

    if (!url) {
      return null;
    }

    const name =
      message?.file_name ||
      message?.fileName ||
      message?.file?.name ||
      "attachment";
    let type =
      message?.file_type || message?.fileType || message?.file?.type || "";

    if (message?.has_image && !type) {
      type = "image/jpeg";
    }

    if (!type && typeof url === "string" && url.startsWith("data:")) {
      type = url.split(";")[0].replace("data:", "");
    }

    // Keep chat-image paths as-is; ChatImagePreview fetches with auth headers.
    let finalUrl = url;
    if (typeof url === "string") {
      const chatImageMatch = url.match(
        /\/api\/(?:chatbot\/)?chat-image\/(\d+)/,
      );
      if (chatImageMatch) {
        if (!type) {type = "image/jpeg";} // Default to JPEG for chat images
      } else if (url.startsWith("/")) {
        // Handle other relative URLs
        const isDevelopment =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";
        const origin = API_ORIGIN;
        finalUrl = isDevelopment ? url : origin + url;
      }
    }

    return { name, type: type || "application/octet-stream", url: finalUrl };
  };

  const loadChat = async (chat: ChatSession) => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {return;}

    campaignFlow.resetFlow();
    setActiveChatSessionId(chat.session_id);
    setCurrentSessionId(chat.session_id);

    const data = await fetchChatMessages(userId, chat.session_id);

    const formatted: Message[] = data.flatMap((m: any) => {
      const campaignFlowSnapshot = parseCampaignFlow(m.campaign_flow);
      if (campaignFlowSnapshot) {
        const timestamp = m.timestamp ?? getTime();
        return [
          {
            sender: "bot",
            timestamp,
            type: "campaignWizard",
            campaignFlowSnapshot,
          } satisfies Message,
          ...buildChoiceAnswerMessages(campaignFlowSnapshot, translate, timestamp),
        ];
      }

      const userMsg: Message = {
        sender: "user",
        text: m.message,
        timestamp: m.timestamp ?? getTime(),
        type: "userMessage",
        file: getFileFromHistory(m),
      };

      const botMsg: Message = {
        sender: "bot",
        text: m.response?.content ?? "",
        timestamp: m.timestamp ?? getTime(),
        type: "botMessage",
      };

      return [userMsg, botMsg];
    });

    setQuery(formatted);
    syncChatbotMessageNumber(formatted.filter(msg => msg.sender === "user").length);

    setFirstMessageSent(true);
    setSuggestedQuestions([]);
    setConversationStep(null);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const quickActions: {
    value: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: "Create New Campaign",
      title: text.quickActionCreateCampaignTitle,
      subtitle: text.quickActionCreateCampaignSubtitle,
      icon: <Volume2 size={20} />,
    },
    {
      value: "Impressions",
      title: text.quickActionAnalyseScreenshotTitle,
      subtitle: text.quickActionAnalyseScreenshotSubtitle,
      icon: <ImagePlus size={20} />,
    },
    {
      value: "Engagement & Likes",
      title: text.quickActionEngagementImpressionsTitle,
      subtitle: text.quickActionEngagementImpressionsSubtitle,
      icon: <ChartLine size={20} />,
    },
    {
      value: "Return on Ad Spend",
      title: text.quickActionRoasTitle,
      subtitle: text.quickActionRoasSubtitle,
      icon: <CircleDollarSign size={20} />,
    },
    {
      value: "Create New List",
      title: text.quickActionCreateListTitle,
      subtitle: text.quickActionCreateListSubtitle,
      icon: <ListPlus size={20} />,
    },
  ];

  const filteredExistingAudiences = React.useMemo(
    () =>
      audiences
        .filter(a => a.name !== CREATE_NEW_LIST_NAME)
        .filter(a => audienceListMatchesSearch(a, audienceListSearch)),
    [audiences, audienceListSearch],
  );

  useEffect(() => {
    if (conversationStep !== "askAudience") {
      setAudienceListSearch("");
    }
  }, [conversationStep]);

  useEffect(() => {
    const hasStoredLiveState = Boolean(sessionStorage.getItem("ai_chat_live_state"));
    const hasLiveChatState =
      Boolean(locationState?.liveChatState?.query?.length) || hasStoredLiveState;
    const shouldPreserve = Boolean(locationState?.preserveSession) && !hasLiveChatState;

    if (!shouldPreserve) {
      resetChatSession();
    }
  }, [locationState]);

  useEffect(() => {
    if (consumedLiveStateRef.current) {return;}

    let liveChatState = locationState?.liveChatState;
    if (!liveChatState?.query?.length) {
      const stored = sessionStorage.getItem("ai_chat_live_state");
      if (stored) {
        try {
          liveChatState = JSON.parse(stored);
        } catch (error) {
          liveChatState = undefined;
        }
      }
    }

    if (!liveChatState?.query?.length) {return;}

    consumedLiveStateRef.current = true;
    sessionStorage.removeItem("ai_chat_live_state");
    resetChatSession();
    setCurrentSessionId(null);
    setQuery(liveChatState.query);
    setFirstMessageSent(
      liveChatState.firstMessageSent ?? liveChatState.query.length > 0,
    );
    setSuggestedQuestions(liveChatState.suggestedQuestions || []);
    setConversationStep(liveChatState.conversationStep ?? null);
  }, [locationState]);

  useEffect(() => {
    const hasStoredLiveState = Boolean(sessionStorage.getItem("ai_chat_live_state"));
    const hasLiveChatState =
      Boolean(locationState?.liveChatState?.query?.length) || hasStoredLiveState;
    const shouldPreserve = Boolean(locationState?.preserveSession) && !hasLiveChatState;
    const sessionId = getActiveChatSessionId();
    const userId = localStorage.getItem("user_id");

    if (!shouldPreserve || !sessionId || !userId) {return;}

    const loadSession = async () => {
      try {
        const data = await fetchChatMessages(userId, sessionId);
        const formatted: Message[] = data.flatMap((m: any) => {
          const userMsg: Message = {
            sender: "user",
            text: m.message,
            timestamp: m.timestamp ?? getTime(),
            type: "userMessage",
            file: getFileFromHistory(m),
          };

          const botMsg: Message = {
            sender: "bot",
            text: m.response?.content ?? "",
            timestamp: m.timestamp ?? getTime(),
            type: "botMessage",
          };

          return [userMsg, botMsg];
        });

        setCurrentSessionId(sessionId);
        setQuery(formatted);
        syncChatbotMessageNumber(formatted.filter(msg => msg.sender === "user").length);
        setFirstMessageSent(true);
        setSuggestedQuestions([]);
        setConversationStep(null);
      } catch (error) {
        message.error(text.unableToLoadSession);
      }
    };

    loadSession();
  }, [locationState, text.unableToLoadSession]);

  useEffect(() => {
    scrollToBottom(messagesEndRef);
  }, [query, loading, exitGuard.isPrompting]);

  useEffect(() => {
    isScheduledOrSentRef.current = isScheduledOrSent;
  }, [isScheduledOrSent]);

  const apiLang = getApiLangFromI18n(i18n);

  useEffect(() => {
    const createNewListOption: AudienceListOption = {
      name: CREATE_NEW_LIST_NAME,
      displayName: text.createNewList,
    };

    const loadAudiences = async () => {
      try {
        const apiKey = localStorage.getItem("api_key") || "";
        const tenantId = localStorage.getItem("tenant_id") || "";

        if (!apiKey || !tenantId) {
          message.error(text.missingApiCredentials);
          return;
        }

        const response = await fetchCampaignListsAndCompany(tenantId, apiKey, apiLang);

        setAudiences(
          registerAudienceListLabels([
            ...toAudienceListOptions(response),
            createNewListOption,
          ]),
        );
      } catch (error) {
        const listOption = (key: string) =>
          translate(`audienceOverview.createSegment.listOptions.${key}`);
        setAudiences(
          registerAudienceListLabels([
            { name: "Loyal Customers", displayName: listOption("loyalCustomers") },
            { name: "High Spenders", displayName: listOption("highSpenders") },
            { name: "Cart Abandoners", displayName: listOption("cartAbandoners") },
            createNewListOption,
          ]),
        );
      }
    };

    loadAudiences();
  }, [apiLang, translate, text.missingApiCredentials, text.createNewList]);

  const handleSelectChat = (chat: ChatSession) => {
    runAfterExitConfirm(() => {
      loadChat(chat);
    });
  };

  // "New Chat" starts a fresh session in the UI without deleting anything on the
  // server. Deleting a chat permanently is done from the history sidebar menu.
  const startNewChat = () => {
    clearedRef.current = true;
    resetChatSession();
    setCurrentSessionId(null);
    setQuery([]);
    setFirstMessageSent(false);
    setConversationStep(null);
    setSuggestedQuestions([]);
    setIsFetching(false);
    setLoading(false);
    setMenuOpen(false);
    setQueryLimitApiMessage(undefined);
    campaignFlow.resetFlow();
  };

  const handleNewChat = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    runAfterExitConfirm(startNewChat);
  };

  // Called by the sidebar after a chat is deleted server-side. If the deleted
  // chat is the one currently open, reset the screen to a fresh new chat.
  const handleChatDeleted = (deletedSessionId: string) => {
    if (deletedSessionId === currentSessionId) {
      runAfterExitConfirm(startNewChat);
    }
  };


  const handleQueryLimitReached = React.useCallback((backendDetail: string) => {
    const messageFromApi = backendDetail.trim();
    setQueryLimitApiMessage(messageFromApi);
    if (backendDetail) {
      console.warn("Chatbot query limit reached:", backendDetail);
    }
    setQuery(prev => {
      const hasNotice = prev.some(msg => msg.type === "limitNotice");
      if (hasNotice) {
        return prev;
      }
      if (!messageFromApi) {
        return prev;
      }
      return [
        ...prev,
        {
          sender: "bot",
          type: "limitNotice",
          text: messageFromApi,
          timestamp: getTime(),
        },
      ];
    });
    setLoading(false);
    setIsFetching(false);
    setSuggestedQuestions([]);
  }, []);

  const messageHandler = React.useMemo(
    () =>
      createMessageHandler({
        textToSend: input,
        conversationStep,
        input,
        selectedFile,
        previewURL,
        campaignName,
        user_id,
        tenant_id,
        clearedRef,
        setQuery,
        setInput,
        setConversationStep,
        setLoading,
        setIsFetching,
        setFirstMessageSent,
        setSuggestedQuestions,
        onQueryLimitReached: handleQueryLimitReached,
      }),
    [
      input,
      conversationStep,
      selectedFile,
      previewURL,
      campaignName,
      user_id,
      tenant_id,
      setQuery,
      setInput,
      setConversationStep,
      setLoading,
      setIsFetching,
      setFirstMessageSent,
      setSuggestedQuestions,
      handleQueryLimitReached,
    ],
  );

  const handleSuggestedQuestionClick = (question: string) => {
    if (question === "Return on Ad Spend") {
      const userMessage: Message = {
        sender: "user",
        text: text.returnOnAdSpend,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.roasReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("roasStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    if (question === "Conversions") {
      const userMessage: Message = {
        sender: "user",
        text: text.conversions,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.conversionsReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("conversionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    if (question === "Impressions") {
      const userMessage: Message = {
        sender: "user",
        text: text.impressions,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.impressionsReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("impressionsStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    if (question === "Engagement & Likes" || question === "Engagements & Likes") {
      const userMessage: Message = {
        sender: "user",
        text: text.engagementAndLikes,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, userMessage]);

      const botMessage: Message & { type?: string } = {
        sender: "bot",
        text: text.engagementReply,
        timestamp: getTime(),
        type: "botQuestion",
      };
      setQuery(prev => [...prev, botMessage]);

      setConversationStep("engagementStep");
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
      return;
    }
    // "Create New Campaign" and "Create New List" are sent to the Ask DataTram
    // API like any other prompt. The backend classifies the message (via the
    // `intent` field) and the UI reacts to that, instead of a hardcoded script.
    setInput(question);
    setSuggestedQuestions([]);
    handleSendMessage(question);
  };

  const handleSendMessage = async (messageText?: string) => {
    if (queryLimitApiMessage !== undefined) {return;}
    const textToSend = messageText ?? input;
    const hasText = textToSend.trim().length > 0;
    const hasFile = !!selectedFile;
    if ((!hasText && !hasFile) || isFetching) {return;}

    // Clear Chat sets this to true to drop stale in-flight replies; reset so new sends map API text.
    clearedRef.current = false;

    if (!firstMessageSent) {setFirstMessageSent(true);}

    try {
      await messageHandler(hasText ? textToSend : "");
    } finally {
      // Always clear input and file regardless of success/failure
      setInput("");
      setSelectedFile(null);
      setPreviewURL(null);
    }
  };


  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || e.buttons === 0) {return;}
    dragged.current = true;
    setPosition({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  };

  const handleMouseUp = () => {
    dragging.current = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleCampaignConfirmation = (option: string) => {
    const userMessage: Message = {
      sender: "user",
      text: option === "Yes" ? text.yes : option === "No" ? text.no : option,
      timestamp: getTime(),
    };
    setQuery(prev => [...prev, userMessage]);

    if (option === "Yes") {
      const wizardMessage: Message = {
        sender: "bot",
        timestamp: getTime(),
        type: "campaignWizard",
      };

      setQuery(prev => [...prev, wizardMessage]);
      setConversationStep(null);
      setFirstMessageSent(true);
      setSuggestedQuestions([]);
      setInput("");
    } else {
      const botMessage: Message = {
        sender: "bot",
        text: text.okayNoProblem,
        timestamp: getTime(),
      };
      setQuery(prev => [...prev, botMessage]);
      setConversationStep(null);
      setSuggestedQuestions([
        "Create New Campaign",
        "Show campaign performance",
        "View reports",
      ]);
      setInputDisabled(false);
    }
  };

  const WaveIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 35 32"
      fill="none"
    >
      <path
        d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div
      ref={chatbotRef}
      className="chatbot-screen-container"
      onClick={() => menuOpen && setMenuOpen(false)}
    >
      <div
        className={`chat-layout-container ${
          isSidebarOpen ? "sidebar-open" : ""
        }`}
      >
        {/* LEFT SIDEBAR */}
        <ChatHistorySidebar
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
          onSelectChat={handleSelectChat}
          onChatDeleted={handleChatDeleted}
          currentSessionId={currentSessionId}
        />

        <div className="chatbox-screen scrollable-chat chat-layout">
          <div className="chatbot-header blue-border">
            <div className="chatbot-header-left">
              <button
                className={`sidebar-btn ${isSidebarOpen ? "open hidden" : ""}`}
                onClick={() => setIsSidebarOpen(prev => !prev)}
                aria-label={text.toggleSidebar}
              >
                <img src="/Icons/sidebar-arrow.svg" alt="" />
              </button>
            </div>

            <div className="chatbox-header-actions">
              <button
                onClick={handleNewChat}
                className="button button--primary new-chat-btn"
              >
                {text.newChat}
              </button>
            </div>
          </div>
          <div
            className={`conversation-area-chatbot ${
              !firstMessageSent && conversationStep === null
                ? "without-suggestions"
                : ""
            }`}
          >
            {!firstMessageSent && conversationStep === null && (
              <>
                <div className="datatram-welcome-container">
                  <div className="datatram-avatar">
                    <Avatar size={35} icon={<WaveIcon />} />
                  </div>

                  <div className="datatram-greeting-title">
                    {text.greetingTitle}
                  </div>
                  <div className="datatram-greeting-subtitle">
                    {text.greetingSubtitle}
                  </div>
                </div>

                <div className="datatram-quick-actions">
                  {quickActions.map((action, idx: number) => (
                    <button
                      key={idx}
                      className="datatram-action-btn"
                      onClick={() => handleSuggestedQuestionClick(action.value)}
                    >
                      <span className="datatram-action-icon">{action.icon}</span>
                      <span className="datatram-action-content">
                        <span className="datatram-action-title">{action.title}</span>
                        <span className="datatram-action-subtitle">{action.subtitle}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div
              className={`chat-messages ${
                !firstMessageSent && conversationStep === null && query.length === 0
                  ? "empty"
                  : ""
              }`}
            >
              {query.map((msg, index) => (
                <div key={index}>
                  {msg.type === "campaignWizard" && (() => {
                    const cancelWizard = () => {
                      setConversationStep(null);
                      setSuggestedQuestions([
                        "Create New Campaign",
                        "Show campaign performance",
                        "View reports",
                      ]);
                    };
                    const resolvedPhase = msg.campaignFlowSnapshot?.phase ?? campaignFlow.phase;
                    const frozenEmailSnapshot =
                      msg.campaignFlowSnapshot?.phase === "email"
                        ? msg.campaignFlowSnapshot.snapshot
                        : undefined;
                    const frozenTemplateSnapshot =
                      msg.campaignFlowSnapshot?.phase === "template"
                        ? msg.campaignFlowSnapshot.snapshot
                        : undefined;
                    const frozenWhatsAppSnapshot =
                      msg.campaignFlowSnapshot?.phase === "whatsapp"
                        ? msg.campaignFlowSnapshot.snapshot
                        : undefined;

                    return (
                      <div className="category-bubble-container">
                        <div className="chatbot-avatar">
                          <Avatar size={35} icon={<WaveIcon />} alt="Chatbot Avatar" />
                        </div>
                        <div className="category-selection-container">
                          {resolvedPhase === "channel" && (
                            <ChatChannelChoice
                              onSelect={campaignFlow.chooseChannel}
                              isAnswered={Boolean(msg.campaignFlowSnapshot)}
                            />
                          )}
                          {resolvedPhase === "setup" && (
                            <div
                              className={`campaign-flow chat-campaign-wizard${
                                msg.campaignFlowSnapshot ? " chat-campaign-wizard--frozen" : ""
                              }`}
                            >
                              <ChatCampaignSetupStep
                                setup={
                                  msg.campaignFlowSnapshot?.phase === "setup" &&
                                  msg.campaignFlowSnapshot.result
                                    ? { ...campaignFlow.setup, ...msg.campaignFlowSnapshot.result }
                                    : campaignFlow.setup
                                }
                                onCancel={cancelWizard}
                                frozen={Boolean(msg.campaignFlowSnapshot)}
                                isChannelLocked
                                onDraftSaved={startNewChat}
                              />
                            </div>
                          )}
                          {resolvedPhase === "email" && (
                            <ChatCampaignWizard
                              wizard={campaignFlow.emailWizard}
                              frozenSnapshot={frozenEmailSnapshot}
                              onBackToSetup={campaignFlow.backToSetup}
                              onDraftSaved={startNewChat}
                            />
                          )}
                          {resolvedPhase === "template" && (
                            <ChatTemplateCampaignWizard
                              wizard={campaignFlow.templateWizard}
                              frozenSnapshot={frozenTemplateSnapshot}
                              onDraftSaved={startNewChat}
                            />
                          )}
                          {resolvedPhase === "whatsappMode" && (
                            <ChatWhatsAppModeChoice
                              onSelect={campaignFlow.chooseWhatsAppMode}
                              isAnswered={Boolean(msg.campaignFlowSnapshot)}
                            />
                          )}
                          {resolvedPhase === "whatsapp" && (
                            <ChatWhatsAppCampaignWizard
                              wizard={campaignFlow.whatsAppWizard}
                              frozenSnapshot={frozenWhatsAppSnapshot}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {msg.type === "confirmCampaign" && (
                    <div className="confirm-buttons">
                      {msg.options?.map((option: string, index: number) => (
                        <button
                          key={index}
                          className={`button ${
                            option === "Yes"
                              ? "button button--primary"
                              : "button button--outline"
                          }`}
                          onClick={() => handleCampaignConfirmation(option)}
                        >
                          {option === "Yes"
                            ? text.yes
                            : option === "No"
                              ? text.no
                              : option}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.type === "menuQuestions" && msg.menu && (
                    <div className="message bot">
                      <div className="message-text">
                        {text.relatedQuestions}
                      </div>
                      <div className="menu-questions-grid">
                        {msg.menu?.questions?.map((q, idx) => (
                          <button
                            key={idx}
                            className="campaign-option-button"
                            onClick={() => handleSendMessage(q.text)}
                          >
                            <img
                              src={q.icon}
                              alt="q-icon"
                              className="menu-question-icon"
                            />
                            <span>{q.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {msg.type === "limitNotice" && msg.text && (
                    <div className="message-row bot system-notice-row">
                      <div className="chatbot-avatar">
                        <Avatar size={35} icon={<WaveIcon />} />
                      </div>
                      <div className="message-bubble system-notice-bubble">
                        {msg.text}
                      </div>
                    </div>
                  )}

                  {/* Default message rendering - shows IMAGE FIRST then TEXT */}
                  {!msg.type ||
                  msg.type === "userMessage" ||
                  msg.type === "botMessage" ||
                  msg.type === "botQuestion" ? (
                    <>
                      {/* SHOW IMAGE FIRST if it exists */}
                      {msg.file &&
                        msg.file.url &&
                        msg.file?.type?.startsWith("image/") && (
                          <div className={`message-row ${msg.sender}`}>
                            {msg.sender === "bot" && (
                              <div className="chatbot-avatar">
                                <Avatar size={35} icon={<WaveIcon />} />
                              </div>
                            )}
                            <div
                              className={`message-bubble ${
                                msg.sender === "bot"
                                  ? "bot-bubble"
                                  : "user-bubble"
                              }`}
                              style={{
                                background: "transparent",
                                padding: msg.text ? "0" : "0",
                                boxShadow: "none",
                              }}
                            >
                              <ChatImagePreview
                                src={msg.file.url}
                                alt={msg.file.name}
                                className="chat-image-preview"
                                onError={e => {
                                  console.error(
                                    "Failed to load image:",
                                    msg.file?.url,
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "400px",
                                  borderRadius: "8px",
                                  marginBottom: msg.text ? "12px" : "0",
                                  display: "block",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          </div>
                        )}

                      {/* SHOW TEXT AFTER IMAGE */}
                      {msg.text && (
                        <div
                          className={`message-row ${msg.sender} ${
                            msg.type === "botQuestion" ? "bot-question" : ""
                          }`}
                        >
                          {msg.sender === "bot" && !msg.file && (
                            <div className="chatbot-avatar">
                              <Avatar size={35} icon={<WaveIcon />} />
                            </div>
                          )}

                          <div
                            className={`message-bubble ${
                              msg.sender === "bot"
                                ? "bot-bubble"
                                : "user-bubble"
                            }`}
                          >
                            {msg.sender === "bot" ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {msg.text}
                              </ReactMarkdown>
                            ) : (
                              msg.text
                            )}
                          </div>
                        </div>
                      )}

                      {msg.campaignChannelOptions && index === query.length - 1 && (
                        <ChatChannelReplies
                          channels={msg.campaignChannelOptions}
                          onReply={handleSendMessage}
                        />
                      )}

                      {/* SHOW NON-IMAGE FILE LINK */}
                      {msg.file && !msg.file?.type?.startsWith("image/") && (
                        <div className={`message-row ${msg.sender}`}>
                          {msg.sender === "bot" && !msg.text && (
                            <div className="chatbot-avatar">
                              <Avatar size={35} icon={<WaveIcon />} />
                            </div>
                          )}
                          <div
                            className={`message-bubble ${
                              msg.sender === "bot"
                                ? "bot-bubble"
                                : "user-bubble"
                            }`}
                          >
                            <a
                              href={msg.file.url || undefined}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="chat-file-link"
                            >
                              ðŸ“Ž {msg.file.name}
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              ))}
              {/* Show audience options ONLY at askAudience step */}

              {/* Audience options ONLY at askAudience step */}
              {conversationStep === "askAudience" && (
                <div className="audience-selection-container">
                  {Array.isArray(audiences) && audiences.length > 0 && (
                    <>
                      <div className="audience-list-heading">
                        {text.selectYourList}
                      </div>
                      {audienceListSearch.trim() &&
                        filteredExistingAudiences.length === 0 && (
                          <p className="audience-list-empty-filter" role="status">
                            {text.noListsMatchSearch}
                          </p>
                        )}
                      <div className="campaign-screen-grid audience-list-grid-with-search">
                        <div
                          className="audience-list-search-cell"
                          dir={isArabic ? "rtl" : "ltr"}
                        >
                          <Search
                            size={12}
                            className="audience-list-search-icon"
                            aria-hidden
                          />
                          <input
                            type="search"
                            className="audience-list-search-input"
                            value={audienceListSearch}
                            onChange={e => setAudienceListSearch(e.target.value)}
                            placeholder={text.searchListsPlaceholder}
                            autoComplete="off"
                            spellCheck={false}
                            aria-label={text.searchListsPlaceholder}
                          />
                        </div>
                        {filteredExistingAudiences.map((audience, idx) => (
                          <button
                            key={`${audience.name}-${idx}`}
                            className="campaign-option-button"
                            /* Send the canonical `name` — segment lookup matches on it, not the label. */
                            onClick={() => handleSendMessage(audience.name)}
                          >
                            {audience.displayName}
                            <span className="arrow">
                              <img src="/Icons/smallarrow.svg" alt="arrow" />
                            </span>
                          </button>
                        ))}
                      </div>
                      {/* TEMP: "Create New List" hidden in the campaign audience step (chatbot flow only). Re-enable by changing `false` back to `true`. */}
                      {false && (
                        <>
                          <div className="custom-list-heading">
                            {text.createCustomList}
                          </div>
                          <div className="campaign-screen-grid">
                            {audiences
                              .filter(aud => aud.name === CREATE_NEW_LIST_NAME)
                              .map((audience, idx) => (
                                <button
                                  key={idx}
                                  className="campaign-option-button"
                                  onClick={() => handleSendMessage(audience.name)}
                                >
                                  {audience.displayName}
                                  <span className="arrow">
                                    <img src="/Icons/smallarrow.svg" alt="arrow" />
                                  </span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {loading && (
                <div className="message-row bot typing-message">
                  <div className="chatbot-avatar">
                    <Avatar
                      size={35}
                      icon={<WaveIcon />}
                      alt="Chatbot Avatar"
                    />
                  </div>
                  <div className="message-bubble bot-bubble typing-bubble">
                    <span className="typing-text">{text.typing}</span>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              {exitGuard.isPrompting && (
                <div className="message-row bot">
                  <div className="chatbot-avatar">
                    <Avatar size={35} icon={<WaveIcon />} />
                  </div>
                  <div className="message-bubble bot-bubble">
                    <strong>{translate("campaignFlow.common.exitTitle")}</strong>
                    <div>{translate("campaignFlow.common.exitDescription")}</div>
                    <div className="confirm-buttons confirm-buttons--end">
                      <button className="button button--primary" onClick={exitGuard.confirmExit}>
                        {translate("campaignFlow.common.exit")}
                      </button>
                      <button className="button button--outline" onClick={exitGuard.cancelExit}>
                        {translate("campaignFlow.common.cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {suggestedQuestions.length > 0 && (
            <div className="compact-suggestions-container">
              <div className="compact-suggestions-header">
                {text.suggestedQuestionsHeader}
              </div>
              <div className="compact-suggestions-scroll">
                {suggestedQuestions.map((question, index) => (
                  <button
                    className="compact-pill-button"
                    key={index}
                    onClick={() => handleSuggestedQuestionClick(question)}
                  >
                    {question === "Create New Campaign"
                      ? text.createNewCampaign
                      : question === "Create New List"
                        ? text.quickActionCreateListTitle
                        : question === "Show campaign performance"
                          ? text.showCampaignPerformance
                          : question === "View reports"
                            ? text.viewReports
                            : question === "Return on Ad Spend"
                              ? text.returnOnAdSpend
                              : question === "Engagement & Likes" ||
                                  question === "Engagements & Likes"
                                ? text.engagementAndLikes
                                : question === "Conversions"
                                  ? text.conversions
                                  : question === "Impressions"
                                    ? text.impressions
                                    : question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* âŒ¨ï¸ Input */}
          <div className="chat-screen-input-shell">
            <ChatInput
              input={input}
              setInput={setInput}
              handleSendMessage={handleSendMessage}
              isFetching={isFetching}
              isInputLocked={queryLimitApiMessage !== undefined}
              inputHelperText={queryLimitApiMessage || undefined}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              previewURL={previewURL}
              setPreviewURL={setPreviewURL}
              showAttachmentHints
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
